import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../auth/auth.service';

/**
 * Redirects from root path '' to the role-specific dashboard (client, cleaner, admin).
 * Used so that http://localhost:8100/ shows the dashboard instead of an empty shell.
 */
export const defaultDashboardGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  await authService.waitForInitialization();

  if (!authService.isAuthenticated()) {
    return router.parseUrl('/login');
  }

  try {
    if (await authService.requiresMfaEnrollment()) {
      return router.parseUrl('/settings?mfa=setup');
    }
  } catch (error) {
    console.error('Failed to evaluate MFA enrollment state in defaultDashboardGuard:', error);
  }

  try {
    if (await authService.requiresMfaVerification()) {
      return router.parseUrl('/login?mfa=required');
    }
  } catch (error) {
    console.error('Failed to evaluate MFA verification state in defaultDashboardGuard:', error);
  }

  await authService.ensureUserDataLoaded();
  if (!(await authService.enforceActiveAccountOrSignOut())) {
    return false;
  }

  const role = authService.userRole();
  if (role) {
    const url = authService.getRedirectUrlForRole(role);
    return router.parseUrl(url);
  }
  return router.parseUrl('/login');
};
